import React, { useState, useRef, useEffect, ReactNode, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface CarouselProps {
  children: ReactNode[];
  itemsPerView?: {
    mobile: number;
    tablet: number;
    desktop: number;
  };
  gap?: number;
  autoPlay?: boolean;
  autoPlayInterval?: number;
}

const Carousel: React.FC<CarouselProps> = ({
  children,
  itemsPerView = { mobile: 1, tablet: 2, desktop: 3 },
  gap = 16,
  autoPlay = false,
  autoPlayInterval = 3000,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsToShow, setItemsToShow] = useState(itemsPerView.desktop);
  const carouselRef = useRef<HTMLDivElement>(null);
  const autoPlayRef = useRef<NodeJS.Timeout | null>(null);

  const totalItems = children.length;
  const maxIndex = Math.max(0, totalItems - itemsToShow);

  // Handle responsive items per view
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setItemsToShow(itemsPerView.mobile);
      } else if (width < 1024) {
        setItemsToShow(itemsPerView.tablet);
      } else {
        setItemsToShow(itemsPerView.desktop);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [itemsPerView]);

  // Auto play functionality
  useEffect(() => {
    if (autoPlay && totalItems > itemsToShow) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      }, autoPlayInterval);

      return () => {
        if (autoPlayRef.current) {
          clearInterval(autoPlayRef.current);
        }
      };
    }
  }, [autoPlay, autoPlayInterval, maxIndex, totalItems, itemsToShow]);

  const handlePrevious = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };

  const handleNext = () => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    setCurrentIndex((prev) => Math.min(maxIndex, prev + 1));
  };

  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;

  // Calculate the width of a single item including gap consideration
  // Each item width = (100% - total gaps) / itemsToShow
  // When sliding, we need to move by (item width + gap)
  const itemWidthPercent = 100 / itemsToShow;
  const gapPerItem = gap / itemsToShow;

  // Instagram-style dynamic dots
  const totalDots = maxIndex + 1;
  const MAX_VISIBLE = 5;
  const DOT_SPACING = 18;
  const showDots = totalItems > itemsToShow;

  // Symmetric container: 5 core dots + 1 peek dot on each side
  const dotsContainerWidth = totalDots <= MAX_VISIBLE
    ? totalDots * DOT_SPACING
    : (MAX_VISIBLE + 2) * DOT_SPACING;

  const dotsTranslateX = useMemo(() => {
    if (totalDots <= MAX_VISIBLE) return 0;

    const totalWidth = totalDots * DOT_SPACING;
    const activeDotCenter = currentIndex * DOT_SPACING + DOT_SPACING / 2;
    const containerCenter = dotsContainerWidth / 2;

    let tx = containerCenter - activeDotCenter;
    tx = Math.max(dotsContainerWidth - totalWidth, Math.min(0, tx));

    return tx;
  }, [currentIndex, totalDots, dotsContainerWidth]);

  // Dot style: edge dots use scale() to stay perfectly circular
  const getDotStyle = (i: number) => {
    const isActive = i === currentIndex;
    const BASE_SIZE = 6;
    const ACTIVE_SIZE = 8;
    const EDGE_ZONE = DOT_SPACING * 1.2;

    if (totalDots <= MAX_VISIBLE) {
      return {
        size: isActive ? ACTIVE_SIZE : BASE_SIZE,
        color: isActive ? '#2563eb' : '#6b7280',
        scale: 1,
        opacity: 1,
      };
    }

    // Visual center of this dot within the container
    const visualCenter = i * DOT_SPACING + DOT_SPACING / 2 + dotsTranslateX;
    const distFromEdge = Math.min(visualCenter, dotsContainerWidth - visualCenter);

    if (isActive) {
      return { size: ACTIVE_SIZE, color: '#2563eb', scale: 1, opacity: 1 };
    }

    if (distFromEdge < EDGE_ZONE) {
      const factor = Math.max(0, distFromEdge / EDGE_ZONE);
      return {
        size: BASE_SIZE,
        color: '#9ca3af',
        scale: 0.4 + factor * 0.6,
        opacity: 0.3 + factor * 0.7,
      };
    }

    return { size: BASE_SIZE, color: '#6b7280', scale: 1, opacity: 1 };
  };

  return (
    <div className="relative group">
      {/* Navigation Buttons */}
      {totalItems > itemsToShow && (
        <>
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 p-2 rounded-full bg-white shadow-lg transition-all duration-300 ${
              canGoPrevious
                ? 'opacity-0 group-hover:opacity-100 hover:scale-110 cursor-pointer'
                : 'opacity-0 cursor-not-allowed'
            }`}
            aria-label="Previous"
          >
            <ChevronLeftIcon className="w-6 h-6 text-gray-700" />
          </button>

          <button
            onClick={handleNext}
            disabled={!canGoNext}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 p-2 rounded-full bg-white shadow-lg transition-all duration-300 ${
              canGoNext
                ? 'opacity-0 group-hover:opacity-100 hover:scale-110 cursor-pointer'
                : 'opacity-0 cursor-not-allowed'
            }`}
            aria-label="Next"
          >
            <ChevronRightIcon className="w-6 h-6 text-gray-700" />
          </button>
        </>
      )}

      {/* Carousel Container - Added padding to prevent shadow/content cut-off */}
      <div className="overflow-hidden py-2 -my-2 px-1 -mx-1" ref={carouselRef}>
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{
            transform: `translateX(calc(-${currentIndex * itemWidthPercent}% - ${currentIndex * gapPerItem}px))`,
            gap: `${gap}px`,
          }}
        >
          {children.map((child, index) => (
            <div
              key={index}
              className="flex-shrink-0"
              style={{
                width: `calc((100% - ${gap * (itemsToShow - 1)}px) / ${itemsToShow})`,
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* Instagram-style Dynamic Dots */}
      {showDots && (
        <div className="flex justify-center mt-3" aria-hidden="true">
          <div
            className="overflow-hidden cursor-pointer"
            style={{ width: `${dotsContainerWidth}px`, height: '16px' }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const clickX = e.clientX - rect.left;
              // Map click position to dot index via translateX offset
              const rawIndex = Math.floor((clickX - dotsTranslateX) / DOT_SPACING);
              const targetIndex = Math.max(0, Math.min(maxIndex, rawIndex));
              if (autoPlayRef.current) clearInterval(autoPlayRef.current);
              setCurrentIndex(targetIndex);
            }}
          >
            <div
              className="flex items-center h-full transition-transform duration-300 ease-out"
              style={{ transform: `translateX(${dotsTranslateX}px)` }}
            >
              {Array.from({ length: totalDots }, (_, i) => {
                const dot = getDotStyle(i);

                return (
                  <div
                    key={i}
                    className="flex-shrink-0 flex items-center justify-center"
                    style={{ width: `${DOT_SPACING}px`, height: '16px' }}
                  >
                    <span
                      className="block rounded-full transition-all duration-300 ease-out"
                      style={{
                        width: `${dot.size}px`,
                        height: `${dot.size}px`,
                        backgroundColor: dot.color,
                        transform: `scale(${dot.scale})`,
                        opacity: dot.opacity,
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Carousel;
